import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const courses = [
  { name: '习近平新时代中国特色社会主义思想概论', short: '习思想概论', color: 'primary' as const },
  { name: '马克思主义基本原理', short: '原理', color: 'accent' as const },
  { name: '中国近现代史纲要', short: '纲要', color: 'primary' as const },
  { name: '思想道德与法治', short: '德法', color: 'accent' as const },
  { name: '毛泽东思想和中国特色社会主义理论体系概论', short: '毛中特概论', color: 'primary' as const },
];

const stats = [
  { value: '5', unit: '门核心课程', icon: 'ri-book-open-line' },
  { value: '3,073', unit: '条整体评价', icon: 'ri-file-list-3-line' },
  { value: '12,039', unit: '条片段级评价', icon: 'ri-stack-line' },
];

const commonIssues = [
  '节奏与时间管理',
  '互动不足',
  '重点不够突出',
  '理论阐释不够深入',
  '案例和视频使用失衡',
];

export default function P67Compliance({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />跨课程覆盖
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            天津实践覆盖的<span className="text-gold-600">不只是一门课程</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            跨课程不是换一个课程名称，而是识别共同教学问题，同时保留每门课程的专业标准
          </p>
        </div>

        {/* Center base + surrounding courses */}
        <div className={`max-w-4xl mx-auto mb-4 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="p-6 md:p-8 glass-liquid-gold rounded-2xl">
            {/* Center base */}
            <div className="flex justify-center mb-5">
              <div className="px-5 py-3 rounded-full glass-gold border border-gold-200/40">
                <span className="text-sm font-body font-bold text-gold-800">思政课AI评价共同底座</span>
              </div>
            </div>

            {/* Surrounding courses */}
            <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-5">
              {courses.map((c, i) => (
                <div
                  key={c.short}
                  className={`px-3 py-2 rounded-xl text-xs font-body font-semibold ${
                    c.color === 'accent'
                      ? 'bg-gold-100/80 text-gold-700 border border-gold-300/40'
                      : 'bg-primary-100/60 text-primary-700 border border-primary-200/40'
                  }`}
                  style={{ transitionDelay: `${400 + i * 100}ms` }}
                  title={c.name}
                >
                  {c.short}
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {stats.map((s, i) => (
                <div
                  key={s.unit}
                  className="text-center p-3 rounded-xl bg-background-100/50 border border-background-200/40"
                  style={{ transitionDelay: `${500 + i * 100}ms` }}
                >
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${s.icon} text-sm text-primary-600`} />
                    <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900">{s.value}</span>
                  </div>
                  <p className="text-xs text-foreground-500 font-body">{s.unit}</p>
                </div>
              ))}
            </div>

            {/* Common issues */}
            <div className="text-center">
              <p className="text-xs font-body text-foreground-500 mb-2">多门课程中反复出现的共性问题</p>
              <div className="flex flex-wrap justify-center gap-1.5">
                {commonIssues.map((issue, i) => (
                  <span
                    key={issue}
                    className="px-2.5 py-1 rounded-full text-xs font-body text-secondary-700 bg-secondary-100/60 border border-secondary-200/30"
                    style={{ transitionDelay: `${600 + i * 60}ms` }}
                  >
                    {issue}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 glass-liquid rounded-xl">
            <p className="text-xs text-foreground-500 font-body leading-relaxed">
              数据来源：专家详细评价结果（脱敏版），不同课程样本规模、专家构成和课程任务不同，不进行课程间平均分排名
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}